import * as L from "leaflet";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { SegmentForm, type SegmentFormData } from "src/forms/SegmentForm";
import { useRoadTypes, useVehicleTypes } from "src/hooks";
import { useBarrierHeights } from "src/hooks/barrierHooks";
import { useSegmentCreateMutation } from "src/hooks/segments/useSegmentCreateMutation";
import { useTimefactors } from "src/hooks/timefactorHooks";
import { useCase } from "src/hooks/useCase";
import { useScenario } from "src/hooks/useScenario";
import { removeAllDrawnLayers } from "src/utils/map.utils";
import wkx from "wkx";

interface Props {
	map: L.Map;
	onClose: () => void;
}

export function TrafficEmissionSegmentCreatePage(props: Props) {
	const { map } = props;

	const { t } = useTranslation();

	const {
		caseId = "",
		scenarioId = "",
		emissionId = "",
	} = useParams<{
		caseId: string;
		scenarioId: string;
		emissionId: string;
	}>();
	const location = useLocation();
	const navigate = useNavigate();

	const { data: acase } = useCase(caseId);
	const { data: roadTypes } = useRoadTypes();
	const { data: vehicleTypes } = useVehicleTypes(scenarioId);
	const { data: timefactors } = useTimefactors();
	const { data: barrierHeights } = useBarrierHeights();
	const createMutation = useSegmentCreateMutation(scenarioId);
	const { data: scenario } = useScenario(scenarioId);

	/**
	 * Effects
	 */
	useEffect(checkDrawnLayerOnInit, [
		caseId,
		scenarioId,
		emissionId,
		navigate,
		location.state,
	]);
	useEffect(drawLayerOnMap, [location.state.drawing, map]);

	/**
	 * Functions
	 */
	function checkDrawnLayerOnInit() {
		const redirectUrl = `/cases/${caseId}/scenarios/${scenarioId}/emissions/traffic/${scenarioId}`;
		if (!location.state?.drawing) navigate(redirectUrl);
	}

	function drawLayerOnMap() {
		// only draw if drawing is provided
		if (!location.state?.drawing) return;

		const layer = L.geoJSON(location.state.drawing);

		layer.addTo(map);

		return function cleanup() {
			layer.removeFrom(map);
		};
	}

	async function handleSave(values: SegmentFormData) {
		if (!location.state.drawing) return;

		const { geometry } = location.state.drawing;
		const linestring = wkx.Geometry.parseGeoJSON(geometry);
		const result = await createMutation.mutateAsync({
			...values,
			lineString_frontendSrid: linestring.toWkt(),
		});

		removeAllDrawnLayers(map);
		const urlBase = `/cases/${caseId}/scenarios/${scenarioId}/emissions/traffic/${emissionId}/segments`;
		if (Array.isArray(result)) {
			navigate(`${urlBase}/bulk`, {
				state: { bulkEdit: result.map((r) => r.id) },
			});
		} else {
			navigate(`${urlBase}/${result.id}`, { state: { action: "rerenderWMS" } });
		}
	}

	return render();

	function render() {
		if (!acase || !vehicleTypes || !timefactors) return null;

		return (
			<SegmentForm
				title={t("label.addSegment")}
				data={{
					roadType: "",
					height_m: 0,
					speed_km_h: 0,
					ifdmTimeFactor: "",
					barrierHeight_m: 0,
					vehicleCounts: vehicleTypes.map((vehicleType) => ({
						vehicleType,
						count: 0,
					})),
				}}
				timefactorType={acase.timefactorType}
				barrierHeights={barrierHeights || []}
				vehicleTypes={vehicleTypes || []}
				roadTypes={roadTypes || []}
				timefactors={timefactors || []}
				onClose={() => props.onClose()}
				onSubmit={handleSave}
				isLoading={createMutation.isLoading}
				errors={createMutation.error?.response?.data}
				isBaselineScenario={scenario?.baseline}
			/>
		);
	}
}
